import React, { useCallback, useEffect, useRef, useState, forwardRef } from 'react';
import {
  Content,
  Header,
  Progress,
  Table,
  TableColumn,
  TableProps,
} from '@backstage/core-components';
import ErrorIcon from '@material-ui/icons/Error';
import { 
  Typography, 
  makeStyles,
  useTheme,
  Button,
  Box,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import RefreshIcon from '@material-ui/icons/Sync';
import {
  useApi,
  identityApiRef,
  configApiRef,
  errorApiRef,
} from '@backstage/core-plugin-api';
import { BackstageTheme } from '@backstage/theme';
import { useServiceRegistryTranslation } from '../../i18n/ServiceRegistryLanguageProvider';
import { tablePaginationLocalization } from '../../i18n/pagination';

interface EventApi {
  displayName: string;
  description: string;
  version: string;
  applicationName: string;
  status: string;
  productName: string;
}

interface CustomError extends Error {
  code?: string;
  status?: number;
}

// Generate unique error reference - matching your ErrorBoundaryFallback pattern
const generateErrorReference = () => {
  const timestamp = Date.now().toString();
  const randomStr = Math.random().toString(36).substring(2, 8).toUpperCase();
  return `${timestamp.slice(-6)}-${randomStr}`;
};

// Matching the styling from ConsumerAppsTable
const useStyles = makeStyles(
  theme => ({
    root: {
      "& $actions": {
        paddingRight: theme.spacing(1),
      },
      "& table $actions": {
        paddingRight: theme.spacing(0),
      }
    },
    actions: {
      display: "flex",
      justifyContent: "right"
    },
    noDataMessage: {
      padding: theme.spacing(2),
      display: "flex",
      justifyContent: "center",
    },
    headerContainer: {
      margin: 0,
    },
    tableContainer: {
      width: '100%',
      fontSize: '0.875rem',
    },
    errorContainer: {
      marginTop: theme.spacing(2),
    },
    errorReference: {
      fontFamily: 'monospace',
      fontSize: '0.875rem',
      backgroundColor: theme.palette.grey[100],
      padding: theme.spacing(1),
      borderRadius: theme.shape.borderRadius,
      marginTop: theme.spacing(1),
    }
  }),
  { name: 'SubscriptionList' },
);

const SubscriptionList = () => {
  const { t } = useServiceRegistryTranslation();
  const classes = useStyles();
  const theme = useTheme<BackstageTheme>();
  
  const config = useApi(configApiRef);
  const baseUrl = config.getString('backend.baseUrl');
   const identityApi = useApi(identityApiRef);
  const errorApi = useApi(errorApiRef);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<CustomError | undefined>();
  const [errorReference, setErrorReference] = useState<string>('');
  const tableRef = useRef<any>();

  // Function to handle errors with unique reference numbers
  const handleError = useCallback((err: Error | CustomError, context?: string) => {
    const reference = generateErrorReference(); // Use the same function as ErrorBoundaryFallback
    setErrorReference(reference);
    
    const customError: CustomError = {
      ...err,
      name: err.name || 'SubscriptionError',
      message: err.message,
      code: reference,
    };
    
    setError(customError);
    
    // Report to Backstage error API for centralized logging
    errorApi.post({
      name: customError.name,
      message: `${context ? `${context}: ` : ''}${customError.message}`,
      stack: err.stack,
    });
    
    console.error(`[${reference}] Error in SubscriptionList${context ? ` (${context})` : ''}:`, err);
  }, [errorApi]);

  // Function to fetch subscription data with query parameters
  const getData = useCallback(async (query: any) => {
    try {
      setLoading(true);
      setError(undefined);
      
      // Make sure query is properly defined with defaults
      const page = query?.page ?? 0;
      const pageSize = query?.pageSize ?? 20;
      const orderBy = query?.orderBy?.field ?? 'displayName';
      const orderDirection = query?.orderDirection ?? 'asc';
      const search = query?.search ?? '';
      
      // Get user authentication token
      const { token } = await identityApi.getCredentials();
      if (!token) {
        throw new Error('Authentication token is unavailable. Please log in again.');
      }

      // Build query parameters for pagination, filtering, and sorting
      const queryParams = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
        sortField: orderBy,
        sortOrder: orderDirection,
        searchQuery: search,
      });

      // Fetch subscriptions from our backend endpoint with query parameters
      const response = await fetch(`${baseUrl}/api/es/getUserSubscriptions?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        const customError: CustomError = new Error(
          `Failed to fetch subscriptions (${response.status}): ${response.statusText}`
        );
        customError.status = response.status;
        
        // Try to parse error response for more details
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            customError.message = errorData.message;
          }
        } catch {
          // Use default error message if parsing fails
        }
        
        throw customError;
      }

      const data = await response.json();
      
      setLoading(false);
      return {
        data: data.subscriptions || [],
        page: page,
        totalCount: data.total || 0,
      };
    } catch (error: any) {
      setLoading(false);
      handleError(error, 'Data Fetch');
      return {
        data: [],
        page: 0,
        totalCount: 0,
      };
    }
  }, [identityApi, baseUrl, handleError]);

  // Define table columns
  const columns: TableColumn<EventApi>[] = [
    {
      title: t('subscriptions.name'),
      field: 'displayName',
      render: (row: Partial<EventApi>) => (
        <Typography variant="body2" style={{ fontWeight: theme.typography.fontWeightMedium }}>
          {row.displayName}
        </Typography>
      ),
      defaultSort: "asc"
    },
    {
      title: t('subscriptions.application'),
      field: 'applicationName',
      render: (row: Partial<EventApi>) => (
        <Typography variant="body2">{row.applicationName}</Typography>
      ),
    },
    {
      title: t('subscriptions.description'),
      field: 'description',
      render: (row: Partial<EventApi>) => (
        <Typography variant="body2">{row.description}</Typography>
      ),
    },
    {
      title: t('subscriptions.version'),
      field: 'version',
      render: (row: Partial<EventApi>) => (
        <Typography variant="body2">{row.version}</Typography>
      ),
    },
    {
      title: t('subscriptions.status'),
      field: 'status',
      render: (row: Partial<EventApi>) => (
        <Typography variant="body2">{row.status}</Typography>
      ),
    },
    {
      title: t('subscriptions.product'),
      field: 'productName',
      render: (row: Partial<EventApi>) => (
        <Typography variant="body2">{row.productName}</Typography>
      ),
    }
  ];

  // Actions component
  const Actions = useCallback(actionProps => (
    <div className={classes.actions}>
      {actionProps.actions.map((action: any, index: number) => (
        <div key={index} onClick={event => action.onClick(event, actionProps.data)}>
          {action.icon}
        </div>
      ))}
    </div>
  ), [classes.actions]);

  // Clear the error state when the component unmounts
  useEffect(() => {
    return () => {
      setError(undefined);
      setErrorReference('');
    };
  }, []);

  const handleRetry = () => {
    setError(undefined);
    setErrorReference('');
    tableRef.current?.onQueryChange();
  };

  // Render error state using custom error styling similar to ErrorBoundaryFallback
  if (error) {
    return (
      <>
        <Header title={t('subscriptions.title')} style={{ margin: 0 }} />
        <Content>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight="400px"
            width="100%"
            style={{ 
              padding: theme.spacing(4),
            }}
          >
            <Box 
              style={{
                width: '100%', 
                maxWidth: '650px',
                padding: theme.spacing(4),
                textAlign: 'center',
                borderRadius: theme.shape.borderRadius,
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Box mb={2}>
                <ErrorIcon style={{ 
                  fontSize: 70, 
                  color: theme.palette.error.main 
                }} />
              </Box>
              
              <Typography 
                variant="h5" 
                gutterBottom
                style={{
                  marginBottom: theme.spacing(2),
                  fontWeight: 600,
                  color: theme.palette.error.main,
                }}
              >
                {t('subscriptions.errorTitle')}
              </Typography>
              
              <Typography 
                variant="body1" 
                paragraph
                style={{
                  marginBottom: theme.spacing(3),
                  color: theme.palette.text.primary,
                }}
              >
                {t('subscriptions.errorMessage')}{' '}
                {error.status === 401 || error.status === 403 
                  ? t('subscriptions.authenticationIssue')
                  : error.status === 404 
                  ? t('subscriptions.notFoundIssue')
                  : error.status && error.status >= 500
                  ? t('subscriptions.serverIssue')
                  : t('subscriptions.tryRefreshing')
                }
                {!(error.status === 401 || error.status === 403 || error.status === 404 || (error.status && error.status >= 500)) && (
                  <>
                    <Typography 
                      component="a"
                      href={`${window.location.origin}/ep-devp/support`}
                      style={{
                        color: '#4A90E2', // Same blue color as in the image
                        textDecoration: 'none',
                        cursor: 'pointer',
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.textDecoration = 'underline';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.textDecoration = 'none';
                      }}
                    >
                      {t('subscriptions.contactSupport')}
                    </Typography>
                    {' '}{t('subscriptions.problemPersists')}
                  </>
                )}
                {(error.status === 401 || error.status === 403 || error.status === 404 || (error.status && error.status >= 500)) && (
                  <>{' '}{t('subscriptions.tryRefreshing')}{' '}<Typography
                      component="a"
                      href={`${window.location.origin}/ep-devp/support`}
                      style={{
                        color: '#4A90E2', // Same blue color as in the image
                        textDecoration: 'none',
                        cursor: 'pointer',
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.textDecoration = 'underline';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.textDecoration = 'none';
                      }}
                    >
                      {t('subscriptions.contactSupport')}
                    </Typography>
                    {' '}{t('subscriptions.problemPersists')}
                  </>
                )}
              </Typography>
              
              {errorReference && (
                <Box 
                  mb={3}
                  p={2}
                  style={{
                    backgroundColor: theme.palette.type === 'dark' 
                      ? theme.palette.grey[900] 
                      : theme.palette.grey[100],
                    borderRadius: theme.shape.borderRadius,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography 
                    variant="subtitle2" 
                    gutterBottom
                    style={{
                      fontWeight: 600,
                      marginBottom: theme.spacing(1),
                      color: theme.palette.text.secondary,
                    }}
                  >
                    {t('subscriptions.errorReference')}
                  </Typography>
                  
                  <Box 
                    component="span"
                    p={1}
                    px={2}
                    style={{
                      fontFamily: 'monospace',
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                      backgroundColor: theme.palette.type === 'dark' 
                        ? theme.palette.grey[800] 
                        : theme.palette.background.paper,
                      borderRadius: theme.shape.borderRadius,
                      display: 'inline-block',
                    }}
                  >
                    #{errorReference}
                  </Box>
                  
                </Box>
              )}
              
              <Box 
                display="flex" 
                justifyContent="center" 
                flexWrap="wrap"
                gap={1}
              >
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={handleRetry}
                  style={{ padding: theme.spacing(1, 3), margin: theme.spacing(0.5) }}
                  startIcon={<RefreshIcon />}
                >
                  {t('subscriptions.tryAgain')}
                </Button>
              </Box>
            </Box>
          </Box>
        </Content>
      </>
    );
  }

  return (
    <>
      <Header
        title={t('subscriptions.title')}
        style={{ margin: 0 }}
      />
      <Content>
        <div className={classes.root}>
          <Table
            tableRef={tableRef}
            columns={columns}
            components={{
              Actions,
            }}
            data={query => getData(query)}
            actions={[
              {
                icon: () => <RefreshIcon />,
                tooltip: t('subscriptions.refresh'),
                isFreeAction: true,
                onClick: () => {
                  tableRef.current?.onQueryChange();
                },
              }
            ]}
            icons={{
              ...Table.icons,
              Search: forwardRef((iconProps, ref) => (
                <SearchIcon {...iconProps} ref={ref} />
              )),
            }}
            localization={{
              pagination: tablePaginationLocalization(t),
              toolbar: { searchPlaceholder: t('subscriptions.search'), searchTooltip: '' },
              // hide label for actions column
              header: { actions: '' },
              body: {
                emptyDataSourceMessage: (
                  <Typography color="textSecondary" className={classes.noDataMessage}>
                    {t('subscriptions.empty')}
                  </Typography>
                )
              },
            }}
            options={{
              actionsColumnIndex: -1,
              actionsCellStyle: {
                paddingRight: theme.spacing(2),
              },
              debounceInterval: 500,
              emptyRowsWhenPaging: false,
              loadingType: 'linear',
              pageSize: 20,
              pageSizeOptions: [10, 20, 50, 100],
              paging: true,
              padding: 'dense',
              showEmptyDataSourceMessage: !loading,
              paginationType: "normal",
              sorting: true,
              thirdSortClick: false,
              headerStyle: {
                fontWeight: theme.typography.fontWeightBold,
                fontSize: '0.875rem',
              },
              rowStyle: {
                fontSize: '0.875rem',
              }
            }}
          />
        </div>
      </Content>
    </>
  );
};

export default SubscriptionList;
